import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  Code,
} from './DocStyles';

const ReactConceitosBasicosDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Conceitos Básicos do React para Iniciantes</Title>

      <Paragraph>
        Este documento apresenta os conceitos fundamentais do React para
        desenvolvedores que nunca trabalharam com esta biblioteca antes.
      </Paragraph>

      <SubTitle>O que é React?</SubTitle>
      <Paragraph>
        React é uma biblioteca JavaScript para construção de interfaces de
        usuário (UI). Foi desenvolvida pelo Facebook e é mantida por eles e pela
        comunidade de desenvolvedores. Diferente de frameworks completos como
        Angular, o React foca especificamente na camada de visualização.
      </Paragraph>

      <SubTitle>Principais Conceitos</SubTitle>

      <SubTitle>1. Componentes</SubTitle>
      <Paragraph>
        No React, tudo é um componente. Um componente é uma parte isolada e
        reutilizável da interface que pode conter HTML, CSS e JavaScript.
      </Paragraph>

      <Paragraph>
        <strong>Tipos de Componentes:</strong>
      </Paragraph>

      <Paragraph>
        <strong>Componentes de Função (Recomendado)</strong>:
      </Paragraph>
      <CodeSnippet
        code={`// Um componente simples de botão
const Botao = (props) => {
  return <button className="botao">{props.texto}</button>;
};`}
      />

      <Paragraph>
        <strong>Componentes de Classe (Legado)</strong>:
      </Paragraph>
      <CodeSnippet
        code={`class Botao extends React.Component {
  render() {
    return <button className="botao">{this.props.texto}</button>;
  }
}`}
      />

      <SubTitle>2. JSX</SubTitle>
      <Paragraph>
        JSX é uma extensão de sintaxe para JavaScript que parece HTML, mas
        permite inserir código JavaScript dentro das tags usando chaves{' '}
        <Code>{'{}'}</Code>.
      </Paragraph>
      <CodeSnippet
        code={`const nome = "Usuário";
const elemento = <h1>Olá, {nome}!</h1>;`}
      />

      <SubTitle>3. Props</SubTitle>
      <Paragraph>
        Props (propriedades) são a forma de passar dados de um componente pai
        para um componente filho.
      </Paragraph>
      <CodeSnippet
        code={`// Componente pai
const App = () => {
  return <Botao texto="Clique Aqui" cor="azul" />;
};

// Componente filho
const Botao = (props) => {
  return (
    <button style={{ backgroundColor: props.cor }}>
      {props.texto}
    </button>
  );
};`}
      />

      <SubTitle>4. Estado (State)</SubTitle>
      <Paragraph>
        O estado é um objeto que contém dados que podem mudar ao longo do tempo
        e afetam o comportamento do componente.
      </Paragraph>
      <CodeSnippet
        code={`import { useState } from 'react';

const Contador = () => {
  // [variável de estado, função para atualizar o estado]
  const [contador, setContador] = useState(0);

  return (
    <div>
      <p>Você clicou {contador} vezes</p>
      <button onClick={() => setContador(contador + 1)}>
        Clique aqui
      </button>
    </div>
  );
};`}
      />

      <SubTitle>5. Hooks</SubTitle>
      <Paragraph>
        Hooks são funções que permitem "conectar-se" aos recursos de estado e
        ciclo de vida do React a partir de componentes funcionais.
      </Paragraph>

      <Paragraph>
        <strong>Hooks Principais:</strong>
      </Paragraph>
      <List>
        <ListItem>
          <strong>useState</strong>: Gerencia o estado local
        </ListItem>
        <ListItem>
          <strong>useEffect</strong>: Executa efeitos colaterais (como chamadas
          de API)
        </ListItem>
        <ListItem>
          <strong>useContext</strong>: Acessa o contexto React
        </ListItem>
        <ListItem>
          <strong>useReducer</strong>: Gerencia estados mais complexos
        </ListItem>
      </List>

      <CodeSnippet
        code={`import { useState, useEffect } from 'react';

const ExemploHooks = () => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    // Este código será executado após a renderização
    // e sempre que o componente for atualizado
    fetch('https://api.exemplo.com/dados')
      .then(resposta => resposta.json())
      .then(dados => setDados(dados));
  }, []); // Array vazio significa que o efeito só executa uma vez

  return (
    <div>
      <h1>Lista de Dados</h1>
      <ul>
        {dados.map(item => (
          <li key={item.id}>{item.nome}</li>
        ))}
      </ul>
    </div>
  );
};`}
      />

      <SubTitle>6. Renderização Condicional</SubTitle>
      <Paragraph>
        Você pode renderizar elementos condicionalmente usando operadores
        JavaScript.
      </Paragraph>
      <CodeSnippet
        code={`const Saudacao = ({ estaLogado }) => {
  return (
    <div>
      {estaLogado 
        ? <h1>Bem-vindo de volta!</h1> 
        : <h1>Por favor, faça login.</h1>
      }
    </div>
  );
};`}
      />

      <SubTitle>7. Listas e Chaves</SubTitle>
      <Paragraph>
        Para renderizar listas de elementos, use o método <Code>map()</Code> e
        forneça uma chave única para cada item.
      </Paragraph>
      <CodeSnippet
        code={`const ListaDeItens = ({ itens }) => {
  return (
    <ul>
      {itens.map(item => (
        <li key={item.id}>{item.nome}</li>
      ))}
    </ul>
  );
};`}
      />

      <SubTitle>Ciclo de Vida de um Componente</SubTitle>
      <Paragraph>
        Em componentes funcionais, o ciclo de vida é gerenciado principalmente
        pelo hook <Code>useEffect</Code>:
      </Paragraph>
      <CodeSnippet
        code={`import { useState, useEffect } from 'react';

const MeuComponente = () => {
  const [dados, setDados] = useState(null);

  // Equivalente a componentDidMount
  useEffect(() => {
    console.log('Componente montado');
    carregarDados();

    // Equivalente a componentWillUnmount
    return () => {
      console.log('Componente será desmontado');
      // Limpeza (ex: cancelar requisições, limpar timers)
    };
  }, []); // Array vazio = executar apenas na montagem

  // Equivalente a componentDidUpdate para a variável dados
  useEffect(() => {
    console.log('Dados atualizados:', dados);
  }, [dados]); // Executar quando 'dados' mudar

  const carregarDados = async () => {
    const resposta = await fetch('https://api.exemplo.com/dados');
    const dadosCarregados = await resposta.json();
    setDados(dadosCarregados);
  };

  return <div>{/* Renderização */}</div>;
};`}
      />

      <SubTitle>TypeScript no React</SubTitle>
      <Paragraph>
        O TypeScript adiciona tipagem estática ao JavaScript, o que ajuda a
        prevenir erros comuns:
      </Paragraph>
      <CodeSnippet
        code={`// Definindo tipos para props
interface BotaoProps {
  texto: string;
  cor?: string; // O ? indica que é opcional
  onClick: () => void;
}

// Usando os tipos definidos
const Botao: React.FC<BotaoProps> = ({ texto, cor = 'blue', onClick }) => {
  return (
    <button 
      style={{ backgroundColor: cor }}
      onClick={onClick}
    >
      {texto}
    </button>
  );
};`}
      />

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        Estes são os conceitos básicos do React que você precisa entender para
        começar a trabalhar com o SmartEntrega. Nos próximos documentos, veremos
        como esses conceitos são aplicados no projeto.
      </Paragraph>
    </DocContainer>
  );
};

export default ReactConceitosBasicosDoc;
